import { Link } from '@inertiajs/react'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { SearchInput } from '@/components/shared/SearchInput'
import { Pagination, PaginationMeta } from '@/components/shared/Pagination'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { useBasePath } from '@/lib/paths'
import { formatDate } from '@/lib/utils'
import { Stamp, Plus } from 'lucide-react'
import type { NotarizationSummary } from '@/types'

type Props = {
  notarizations: NotarizationSummary[]
  search: string
  pagination: PaginationMeta
}

function StageBadge({ stage }: { stage: string }) {
  const config: Record<string, { label: string; className: string }> = {
    application: { label: 'Application', className: 'bg-secondary text-secondary-foreground' },
    payment: { label: 'Paid', className: 'bg-yellow-600 text-white' },
    appointment: { label: 'Appointment', className: 'bg-blue-600 text-white' },
    session: { label: 'Session', className: 'bg-purple-600 text-white' },
    completed: { label: 'Completed', className: 'bg-green-600 text-white' },
  }
  const { label, className } = config[stage] || { label: stage, className: 'bg-secondary text-secondary-foreground' }
  return <Badge className={`font-normal text-xs ${className}`}>{label}</Badge>
}

function SessionModeBadge({ mode }: { mode: string }) {
  const label = mode === 'ron' ? 'RON' : 'In Person'
  return <Badge variant="outline" className="font-normal text-xs">{label}</Badge>
}

export default function NotarizationsIndex({ notarizations, search, pagination }: Props) {
  const basePath = useBasePath()

  return (
    <DashboardLayout title="Notarizations" breadcrumbs={[{ label: 'Notarizations' }]}>
      <div>
        <div className="mb-8">
          <h1 className="text-xl font-semibold tracking-tight">Notarizations</h1>
          <p className="text-sm text-muted-foreground">Manage your notarization requests</p>
          <div className="flex items-center gap-2 mt-3">
            <Button asChild size="sm">
              <Link href={`${basePath}/notarizations/new`}>
                <Plus className="w-3.5 h-3.5 mr-1.5" />
                New Notarization
              </Link>
            </Button>
          </div>
        </div>

        <div className="mb-4">
          <SearchInput value={search} placeholder="Search notarizations..." />
        </div>

        {notarizations.length === 0 ? (
          <div className="border rounded-lg py-16 text-center">
            <Stamp className="w-6 h-6 mx-auto mb-3 text-muted-foreground/30" />
            <h3 className="text-sm font-medium text-foreground mb-1">No notarizations yet</h3>
            <p className="text-sm text-muted-foreground mb-4">Get started by requesting a notarization</p>
            <Button asChild size="sm">
              <Link href={`${basePath}/notarizations/new`}>
                <Plus className="w-3.5 h-3.5 mr-1.5" />
                Get Started
              </Link>
            </Button>
          </div>
        ) : (
          <div className="border rounded-lg">
            {notarizations.map((n, i) => (
              <Link
                key={n.id}
                href={`${basePath}/notarizations/${n.id}`}
                className={`flex items-center justify-between px-4 py-3 hover:bg-muted/50 transition-colors ${i > 0 ? 'border-t' : ''}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center shrink-0">
                    <Stamp className="w-4 h-4 text-muted-foreground" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-foreground truncate">{n.business_name}</span>
                      <StageBadge stage={n.stage} />
                      <SessionModeBadge mode={n.session_mode} />
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {n.type_name} {n.jurisdiction_label ? `· ${n.jurisdiction_label}` : ''} · {formatDate(n.created_at)}
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        <Pagination pagination={pagination} />
      </div>
    </DashboardLayout>
  )
}
